const baseURL = import.meta.env.VITE_API_URL ?? '/api'
const authBaseURL = import.meta.env.VITE_AUTH_URL ?? '/auth'

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  skipAuth?: boolean
}

interface CachedToken {
  token: string
  exp: number
}

let cached: CachedToken | null = null
let inflight: Promise<string | null> | null = null

async function fetchToken(): Promise<string | null> {
  const res = await fetch(`${authBaseURL}/api/auth/token`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  })
  if (!res.ok) return null
  const json = (await res.json()) as { token?: string }
  if (!json.token) return null
  let exp = Date.now() + 9 * 60 * 1000
  try {
    const payload = JSON.parse(atob(json.token.split('.')[1] ?? '')) as { exp?: number }
    if (typeof payload.exp === 'number') exp = payload.exp * 1000 - 30_000
  } catch {}
  cached = { token: json.token, exp }
  return json.token
}

export async function getAccessToken(force = false): Promise<string | null> {
  if (!force && cached && cached.exp > Date.now()) return cached.token
  if (inflight) return inflight
  inflight = fetchToken().finally(() => {
    inflight = null
  })
  return inflight
}

export function clearAccessToken(): void {
  cached = null
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = new URL(`${baseURL}${path}`, window.location.origin)
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) url.searchParams.set(k, String(v))
    }
  }
  const headers = new Headers(opts.headers)
  const init: RequestInit = { headers, credentials: 'include' }
  if (opts.method !== undefined) init.method = opts.method
  if (opts.signal !== undefined) init.signal = opts.signal
  if (opts.cache !== undefined) init.cache = opts.cache
  if (opts.mode !== undefined) init.mode = opts.mode
  if (opts.redirect !== undefined) init.redirect = opts.redirect
  if (opts.body !== undefined) {
    headers.set('content-type', 'application/json')
    init.body = JSON.stringify(opts.body)
  }
  if (!opts.skipAuth && !headers.has('authorization')) {
    const token = await getAccessToken()
    if (token) headers.set('authorization', `Bearer ${token}`)
  }
  let res = await fetch(url, init)
  if (res.status === 401 && !opts.skipAuth) {
    clearAccessToken()
    const token = await getAccessToken(true)
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
      res = await fetch(url, init)
    }
  }
  if (!res.ok) {
    type ErrorPayload = {
      error?: { message?: string; code?: string; details?: unknown }
      message?: string
      code?: string
    }
    let payload: ErrorPayload | null = null
    try {
      payload = (await res.json()) as ErrorPayload
    } catch {}
    const message =
      payload?.error?.message ?? payload?.message ?? `${res.status} ${res.statusText}`
    throw new ApiError(
      message,
      res.status,
      payload?.error?.code ?? payload?.code,
      payload?.error?.details,
    )
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export interface ApiEnvelope<T> {
  ok: true
  data: T
}
